import { api } from './index';

export type ContractType = 'REVENUE' | 'EXPENDITURE';
export type ContractCollectionTiming = 'PREPAID' | 'POSTPAID';
export type ContractStatus = 'PENDING_SIGNATURE' | 'SIGNED' | 'LIQUIDATED';
export type ContractDisplayStatus =
  | 'SIGNED'
  | 'EXPIRING_SOON'
  | 'EXPIRED'
  | 'PENDING_SIGNATURE'
  | 'LIQUIDATED';

export type ContractFileKind = 'CONTRACT' | 'AMENDMENT' | 'APPENDIX' | 'OTHER';

export type ContractAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'FILE_UPLOAD'
  | 'FILE_DELETE'
  | 'GENERATE_INVOICES'
  | 'IMPORT'
  | 'EXPORT';

export interface ContractFile {
  id: string;
  contractId: string;
  fileUrl: string;
  fileName: string;
  mimeType?: string | null;
  fileKind: ContractFileKind;
  notes?: string | null;
  uploadedById?: string | null;
  createdAt: string;
  uploader?: { id: string; name?: string | null; email?: string | null } | null;
}

export interface ContractInvoiceGenerationPreview {
  contractId: string;
  contractCode: string;
  periods: string[];
  amountPerPeriod: number;
  baseAmountPerPeriod: number;
  periodsToCreate: string[];
  periodsToSync: string[];
  priorPaidPeriods: string[];
  warnings: string[];
  manualSchedule?: boolean;
  manualScheduleComplete?: boolean;
}

export interface ContractActionLog {
  id: string;
  buildingId: string;
  contractId?: string | null;
  contractCode?: string | null;
  contractType?: ContractType | null;
  action: ContractAction;
  summary: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  creator?: { id: string; name?: string | null; email?: string | null } | null;
}

export interface Contract {
  id: string;
  buildingId: string;
  contractType: ContractType;
  code: string;
  name: string;
  notes?: string | null;
  customerName?: string | null;
  vendorName?: string | null;
  feeCategoryId?: string | null;
  valueBeforeVat: number;
  valueWithVat: number;
  totalDiscount: number;
  totalVat: number;
  vatPercent: number;
  startDate: string;
  endDate?: string | null;
  liquidatedDate?: string | null;
  contractTerm?: string | null;
  signedDate?: string | null;
  status: ContractStatus;
  contractKind?: string | null;
  templateCode?: string | null;
  approvalWorkflow?: string | null;
  isVerified: boolean;
  parentContractCode?: string | null;
  paymentCycleMonths: number;
  collectionTiming?: ContractCollectionTiming;
  lastInvoicedPeriod?: string | null;
  /** Kỳ thu cuối column: prior marker or latest paid invoice kỳ */
  lastCollectionPeriod?: string | null;
  priorCollectedAmount?: number;
  lastRecordedBillingPeriod?: string | null;
  priorCollectionPostedAmount?: number;
  usesManualInvoiceSchedule?: boolean;
  invoiceCollected?: number;
  files?: ContractFile[];
  createdAt: string;
  updatedAt: string;
  displayStatus: ContractDisplayStatus;
  apartmentCodes: string;
  collected: number;
  remaining: number;
  pendingInvoices: number;
  feeCategory?: { id: string; feeCode?: string | null; name: string } | null;
  creator?: { id: string; name: string } | null;
  apartments?: Array<{
    apartment: { id: string; code: string; name?: string | null; ownerName?: string | null };
  }>;
}

export interface ContractStatusSummary {
  status: ContractDisplayStatus;
  count: number;
  totalValue: number;
  collected: number;
  remaining: number;
  pendingInvoices: number;
}

export interface CreateContractPayload {
  buildingId: string;
  contractType: ContractType;
  code: string;
  name: string;
  notes?: string;
  customerName?: string;
  vendorName?: string;
  feeCategoryId?: string;
  valueBeforeVat?: number;
  valueWithVat?: number;
  totalDiscount?: number;
  totalVat?: number;
  vatPercent?: number;
  startDate: string;
  endDate?: string;
  liquidatedDate?: string;
  contractTerm?: string;
  signedDate?: string;
  status?: ContractStatus;
  contractKind?: string;
  templateCode?: string;
  approvalWorkflow?: string;
  isVerified?: boolean;
  parentContractCode?: string;
  paymentCycleMonths?: number;
  collectionTiming?: ContractCollectionTiming;
  priorCollectedAmount?: number;
  lastRecordedBillingPeriod?: string;
  priorCollectionProjectBankAccountId?: string;
  apartmentIds?: string[];
}

export interface ContractInvoiceItem {
  id: string;
  contractId: string | null;
  contractCode?: string;
  contractName?: string;
  customerName?: string | null;
  apartmentId: string;
  apartmentCode: string;
  billingPeriod: string;
  amount: number;
  baseAmount?: number | null;
  vatAmount?: number | null;
  paidAmount: number;
  status: string;
  paymentStatus: 'PAID' | 'PARTIAL' | 'UNPAID';
  dueDate: string;
  notes?: string | null;
  feeCategoryCode?: string | null;
  feeCategoryName?: string | null;
  createdAt: string;
  /** No phiếu thu; PAID lines may be đã thu trước HT markers only. */
  canDeleteWithoutReceipt?: boolean;
}

export const contractsApi = {
  getStatusSummary: (buildingId: string, contractType: ContractType) =>
    api.get<ContractStatusSummary[]>('/contracts/status-summary', {
      params: { buildingId, contractType },
    }).then((r) => r.data),

  list: (params: {
    buildingId: string;
    contractType: ContractType;
    status?: ContractStatus;
    displayStatus?: ContractDisplayStatus;
    search?: string;
    apartmentId?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<{ items: Contract[]; total: number; page: number; limit: number; totalPages: number }>(
      '/contracts',
      { params },
    ).then((r) => r.data),

  getById: (id: string) => api.get<Contract>(`/contracts/${id}`).then((r) => r.data),

  create: (payload: CreateContractPayload) => api.post<Contract>('/contracts', payload).then((r) => r.data),

  update: (id: string, payload: Partial<CreateContractPayload>) =>
    api.patch<Contract>(`/contracts/${id}`, payload).then((r) => r.data),

  remove: (id: string) => api.delete(`/contracts/${id}`).then((r) => r.data),

  previewGenerateInvoices: (id: string) =>
    api
      .get<ContractInvoiceGenerationPreview>(`/contracts/${id}/generate-invoices-preview`)
      .then((r) => r.data),

  createManualContractInvoices: (
    id: string,
    body: { lines: Array<{ billingPeriod: string; amount: number }>; replaceExisting?: boolean },
  ) =>
    api
      .post<{ created: number; billingPeriods: string[]; usesManualInvoiceSchedule: boolean }>(
        `/contracts/${id}/manual-invoices`,
        body,
      )
      .then((r) => r.data),

  generateInvoices: (id: string, body?: { billingPeriod?: string; force?: boolean }) =>
    api
      .post<{
        created: number;
        updated?: number;
        inSync?: boolean;
        billingPeriod?: string;
        billingPeriods?: string[];
        warnings?: string[];
        schedulePeriodCount?: number;
        amountPerPeriod?: number;
      }>(
        `/contracts/${id}/generate-invoices`,
        body ?? {},
      )
      .then((r) => r.data),

  updateContractInvoiceAmount: (
    invoiceId: string,
    body: { amount: number; baseAmount?: number; vatAmount?: number },
  ) => api.patch(`/contracts/invoices/${invoiceId}/amount`, body).then((r) => r.data),

  deleteContractInvoice: (invoiceId: string) =>
    api.delete<{ success: boolean }>(`/contracts/invoices/${invoiceId}`).then((r) => r.data),

  getPaymentContext: (id: string) => api.get(`/contracts/${id}/payment-context`).then((r) => r.data),

  listForInvoice: (apartmentId: string, feeCategoryId: string) =>
    api
      .get<Array<{
        id: string;
        code: string;
        name: string;
        customerName?: string | null;
        amountPerPeriod: number;
        baseAmountPerPeriod: number;
        vatAmountPerPeriod: number;
        paymentCycleMonths: number;
        lastInvoicedPeriod?: string | null;
      }>>('/contracts/for-invoice', { params: { apartmentId, feeCategoryId } })
      .then((r) => r.data),

  createExpenditureVoucher: (
    id: string,
    body: { amount: number; projectBankAccountId: string; paymentMethod?: string; notes?: string },
  ) => api.post(`/contracts/${id}/create-expenditure-voucher`, body).then((r) => r.data),

  exportContracts: async (buildingId: string, contractType: ContractType): Promise<void> => {
    const response = await api.get('/contracts/export', {
      params: { buildingId, contractType },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename =
      filenameMatch?.[1] ||
      `contracts-${contractType.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.json`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  importContracts: (
    buildingId: string,
    contractType: ContractType,
    file: File,
  ): Promise<{ created: number; updated: number; skipped: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId);
    formData.append('contractType', contractType);
    return api
      .post('/contracts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },

  listContractFiles: (id: string) =>
    api.get<ContractFile[]>(`/contracts/${id}/files`).then((r) => r.data),

  uploadContractFile: (
    id: string,
    file: File,
    options?: { fileKind?: ContractFileKind; notes?: string },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.fileKind) formData.append('fileKind', options.fileKind);
    if (options?.notes) formData.append('notes', options.notes);
    return api
      .post<ContractFile>(`/contracts/${id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  removeContractFile: (contractId: string, fileId: string) =>
    api.delete(`/contracts/${contractId}/files/${fileId}`).then((r) => r.data),

  listContractInvoices: (
    buildingId: string,
    params?: {
      contractId?: string;
      contractSearch?: string;
      apartmentId?: string;
      apartmentSearch?: string;
      billingPeriod?: string;
      paymentStatus?: 'PAID' | 'PARTIAL' | 'UNPAID';
      page?: number;
      limit?: number;
    },
  ) =>
    api
      .get<{
        items: ContractInvoiceItem[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>('/contracts/invoices', { params: { buildingId, ...params } })
      .then((r) => r.data),

  getActionLogs: (
    buildingId: string,
    params?: { contractId?: string; contractType?: ContractType; page?: number; limit?: number },
  ) =>
    api
      .get<import('./paginatedHistory').PaginatedHistoryResponse<ContractActionLog>>(
        '/contracts/action-logs',
        {
          params: { buildingId, ...params },
        },
      )
      .then((r) => r.data),
};
